import { z } from "zod";
/**
 * Run progress model (ENG-367)
 *
 * A single, shared shape that describes the live progress of a planning run
 * or a build run. The API produces it and the web app shows it, so this file
 * must stay free of server-only imports.
 *
 * All timestamps are ISO 8601 strings.
 */
const isoTimestamp = z.string().datetime({ offset: true });
/**
 * Which kind of run the progress belongs to.
 * - `planning`: the planner turning an idea into an epic plan
 * - `build`: the orchestrator building the epic's features
 */
export const runKindSchema = z.enum(["planning", "build"]);
/**
 * Status of a single stage within a run.
 */
export const runStageStatusSchema = z.enum(["pending", "active", "completed", "failed", "skipped"]);
/**
 * Overall state of a run.
 * - `pending`: the run exists but has not reported any progress yet (also
 *   used when stored progress is missing or malformed).
 * - `stalled`: the run is still marked active but has not reported progress
 *   for longer than the stall threshold.
 */
export const runStateSchema = z.enum([
    "pending",
    "active",
    "completed",
    "failed",
    "cancelled",
    "stalled",
]);
/**
 * One stage of a run (for example "Interview", "Draft plan", or a feature
 * being built).
 */
export const runProgressStageSchema = z.object({
    /** Stable machine key for the stage, unique within the run. */
    key: z.string().min(1),
    /** Human-readable label shown in the UI. */
    label: z.string().min(1),
    /** Current status of the stage. */
    status: runStageStatusSchema,
    /** Optional short detail line (for example "3 of 7 tasks"). */
    detail: z.string().nullable(),
    /** When the stage started, or null if it has not started yet. */
    startedAt: isoTimestamp.nullable(),
    /** When the stage finished, or null if it has not finished yet. */
    completedAt: isoTimestamp.nullable(),
});
/**
 * Estimated time remaining for a run, based on past runs.
 */
export const runEtaSchema = z.object({
    /** Estimated seconds left, or null when no estimate can be made. */
    secondsRemaining: z.number().int().nonnegative().nullable(),
    /** How much to trust the estimate, based mostly on sample size. */
    confidence: z.enum(["low", "medium", "high"]),
    /** Number of past runs the estimate is based on. */
    sampleSize: z.number().int().nonnegative(),
    /** True when the run has already taken longer than the estimate. */
    overrun: z.boolean(),
});
/**
 * Live progress of a planning or build run, without the cross-field checks.
 *
 * Use this when you need to `.extend()`, `.pick()` or read `.shape`; zod
 * refinements return a `ZodEffects`, which does not support those. Validate
 * real payloads with {@link runProgressSchema}.
 */
export const runProgressBaseSchema = z.object({
    // --- Identity ---
    /** Which kind of run this is. */
    runKind: runKindSchema,
    // --- Stages ---
    /** Ordered list of the run's stages. */
    stages: z.array(runProgressStageSchema),
    /** Key of the stage currently running, or null if none is running. */
    currentStageKey: z.string().min(1).nullable(),
    // --- Counts ---
    /** Units of work finished (stages for planning, tasks for build). */
    completed: z.number().int().nonnegative(),
    /** Total units of work in the run. */
    total: z.number().int().nonnegative(),
    // --- State ---
    /** Overall state of the run. */
    state: runStateSchema,
    /** Why the run failed or stalled, or null otherwise. */
    failureReason: z.string().nullable(),
    // --- Timing ---
    /** When the run started. */
    startedAt: isoTimestamp,
    /** When progress was last reported. */
    updatedAt: isoTimestamp,
    /** Estimated time remaining, or null when there is no estimate. */
    eta: runEtaSchema.nullable(),
});
/**
 * Live progress of a planning or build run.
 *
 * Adds two checks to {@link runProgressBaseSchema}: `completed` must not be
 * greater than `total`, and `currentStageKey` must be null or one of the
 * stage keys.
 */
export const runProgressSchema = runProgressBaseSchema
    .refine((p) => p.completed <= p.total, {
    message: "completed must not be greater than total",
    path: ["completed"],
})
    .refine((p) => p.currentStageKey === null || p.stages.some((s) => s.key === p.currentStageKey), {
    message: "currentStageKey must match one of the stage keys",
    path: ["currentStageKey"],
});
//# sourceMappingURL=run-progress.js.map